import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import {
  ArrowLeft, Sparkles, MessageSquare, Send, Plus, Bot, PauseCircle, PlayCircle, UserCheck,
} from "lucide-react";
import { crmApi } from "../../lib/crmApi";
import {
  Chip, Field, inputCls, SERVICE_CATEGORIES, LEAD_STATUSES, LEAD_SCORES, label,
} from "./crmUi";

const SENDER_STYLES = {
  customer: { label: "Customer", cls: "bg-white border-[#6E1F2C]/15" },
  ai: { label: "AI", cls: "bg-[#D4A963]/10 border-[#D4A963]/40" },
  sales_staff: { label: "Sales", cls: "bg-[#6E1F2C]/5 border-[#6E1F2C]/25" },
  admin: { label: "Admin", cls: "bg-[#56151F]/5 border-[#56151F]/25" },
};

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const { actor } = useOutletContext();
  const [lead, setLead] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [convos, setConvos] = useState([]);
  const [staff, setStaff] = useState([]);
  const [err, setErr] = useState("");
  const [aiBusy, setAiBusy] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [remark, setRemark] = useState("");
  const [msg, setMsg] = useState({ sender_type: "customer", message_text: "" });
  const [newTask, setNewTask] = useState(null);
  const [waDraft, setWaDraft] = useState("");
  const [waBusy, setWaBusy] = useState("");
  const [waMsg, setWaMsg] = useState("");

  const reload = useCallback(() => {
    crmApi.getLead(leadId).then((d) => { setLead(d.lead); setTasks(d.tasks); }).catch((e) => setErr(e.message));
    crmApi.conversations(leadId).then((d) => setConvos(d.conversations)).catch(() => {});
  }, [leadId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { crmApi.staff().then((d) => setStaff(d.staff)).catch(() => {}); }, []);
  useEffect(() => { setWaDraft(lead?.ai_draft_reply || ""); }, [lead?.ai_draft_reply]);

  const sendWa = async () => {
    if (!waDraft.trim()) return;
    setWaBusy("send"); setWaMsg("");
    try {
      await crmApi.sendWhatsApp(leadId, waDraft.trim());
      setWaMsg("Sent to WhatsApp ✓");
      setWaDraft("");
      reload();
    } catch (e) { setWaMsg(`Failed: ${e.message}`); }
    finally { setWaBusy(""); }
  };

  const regenerateWa = async () => {
    setWaBusy("regen"); setWaMsg("");
    try {
      const d = await crmApi.aiDraftReply(leadId);
      setWaDraft(d.draft);
    } catch (e) { setWaMsg(`Failed: ${e.message}`); }
    finally { setWaBusy(""); }
  };

  const patch = async (changes) => {
    try {
      const d = await crmApi.updateLead(leadId, changes);
      setLead(d.lead);
    } catch (e) { setErr(e.message); }
  };

  const runAi = async (kind) => {
    setAiBusy(kind); setErr(""); setAiResult(null);
    try {
      if (kind === "score") {
        const d = await crmApi.aiScore(leadId);
        setAiResult({ kind, ...d.suggestion });
      } else if (kind === "summary") {
        const d = await crmApi.aiSummary(leadId);
        setAiResult({ kind, text: d.summary });
        reload();
      } else {
        const d = await crmApi.aiDraftReply(leadId);
        setAiResult({ kind, text: d.draft });
      }
    } catch (e) { setErr(e.message); }
    finally { setAiBusy(""); }
  };

  const addRemark = async () => {
    if (!remark.trim()) return;
    try {
      await crmApi.addRemark(leadId, remark.trim());
      setRemark("");
      reload();
    } catch (e) { setErr(e.message); }
  };

  const addMessage = async () => {
    if (!msg.message_text.trim()) return;
    try {
      await crmApi.addConversation(leadId, msg);
      setMsg({ ...msg, message_text: "" });
      reload();
    } catch (e) { setErr(e.message); }
  };

  const saveTask = async () => {
    try {
      await crmApi.createTask({ ...newTask, lead_id: leadId });
      setNewTask(null);
      reload();
    } catch (e) { setErr(e.message); }
  };

  if (!lead) {
    return <p className="text-xs text-[#7A6B5F]">{err || "Loading…"}</p>;
  }

  return (
    <div data-testid="crm-lead-detail">
      <Link to="/admin/crm/leads" className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C] mb-4">
        <ArrowLeft size={12} /> All leads
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#A6822F]">{lead.lead_ref}</p>
          <h1 className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">{lead.customer_name || "Unknown customer"}</h1>
          <p className="text-sm text-[#3E2A22] mt-1">
            {lead.whatsapp_number && (
              <a href={`https://wa.me/${lead.whatsapp_number.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-[#6E1F2C] hover:underline">
                {lead.whatsapp_number}
              </a>
            )}
            {lead.location ? ` · ${lead.location}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Chip list={LEAD_SCORES} value={lead.lead_score} testid="crm-lead-score-chip" />
          <Chip list={LEAD_STATUSES} value={lead.lead_status} testid="crm-lead-status-chip" />
          {lead.possible_duplicate && (
            <span data-testid="crm-lead-dup-chip" className="text-[9px] tracking-[0.14em] uppercase px-2 py-0.5 border border-[#A6822F]/50 text-[#A6822F] bg-[#A6822F]/10">
              Possible duplicate
            </span>
          )}
          {lead.human_takeover && (
            <span className="text-[9px] tracking-[0.14em] uppercase px-2 py-0.5 border border-[#A3141A]/40 text-[#A3141A] bg-[#A3141A]/5 inline-flex items-center gap-1">
              <UserCheck size={10} /> Human takeover
            </span>
          )}
          {lead.ai_paused && (
            <span className="text-[9px] tracking-[0.14em] uppercase px-2 py-0.5 border border-[#7A6B5F]/40 text-[#7A6B5F] inline-flex items-center gap-1">
              <PauseCircle size={10} /> AI paused
            </span>
          )}
        </div>
      </div>

      {err && <p className="text-xs text-[#A3141A] mb-3" data-testid="crm-lead-error">{err}</p>}

      {/* Controls row */}
      <div className="bg-white border border-[#6E1F2C]/10 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Lead status">
          <select data-testid="crm-lead-status-select" className={inputCls} value={lead.lead_status || ""} onChange={(e) => patch({ lead_status: e.target.value })}>
            {LEAD_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Lead score">
          <select data-testid="crm-lead-score-select" className={inputCls} value={lead.lead_score || ""} onChange={(e) => patch({ lead_score: e.target.value })}>
            {LEAD_SCORES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Assigned salesperson">
          <select
            data-testid="crm-lead-assign-select"
            className={inputCls}
            value={lead.assigned_salesperson || ""}
            disabled={actor.type === "staff"}
            onChange={(e) => patch({ assigned_salesperson: e.target.value || null })}
          >
            <option value="">Unassigned</option>
            {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {lead.ai_suggested_salesperson && !lead.assigned_salesperson && (
            <button
              type="button"
              data-testid="crm-lead-assign-suggestion"
              onClick={() => actor.type !== "staff" && lead.ai_suggested_salesperson_id && patch({ assigned_salesperson: lead.ai_suggested_salesperson_id })}
              className="text-[10px] text-[#A6822F] mt-1 hover:text-[#6E1F2C] text-left"
            >
              AI suggests: {lead.ai_suggested_salesperson}{actor.type !== "staff" ? " — click to assign" : ""}
            </button>
          )}
        </Field>
        <Field label="Next follow-up">
          <input type="date" data-testid="crm-lead-followup-date" className={inputCls} value={lead.next_followup_date || ""} onChange={(e) => patch({ next_followup_date: e.target.value })} />
        </Field>
        <Field label="Site visit date">
          <input type="date" className={inputCls} value={lead.site_visit_date || ""} onChange={(e) => patch({ site_visit_date: e.target.value })} />
        </Field>
        <Field label="Quotation status">
          <select className={inputCls} value={lead.quotation_status || ""} onChange={(e) => patch({ quotation_status: e.target.value })}>
            <option value="">—</option>
            {["not_started", "pending", "sent", "revised", "approved"].map((q) => <option key={q} value={q}>{q.replace("_", " ")}</option>)}
          </select>
        </Field>
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => patch({ human_takeover: !lead.human_takeover, lead_status: !lead.human_takeover ? "human_takeover" : lead.lead_status })}
            data-testid="crm-lead-takeover-btn"
            className={`flex-1 px-3 py-2 text-[9px] tracking-[0.16em] uppercase border ${
              lead.human_takeover ? "bg-[#A3141A] text-white border-[#A3141A]" : "border-[#A3141A]/50 text-[#A3141A] hover:bg-[#A3141A]/10"
            }`}
          >
            {lead.human_takeover ? "Takeover ON" : "Mark takeover"}
          </button>
          <button
            type="button"
            onClick={() => patch({ ai_paused: !lead.ai_paused })}
            data-testid="crm-lead-aipause-btn"
            className={`flex-1 px-3 py-2 text-[9px] tracking-[0.16em] uppercase border inline-flex items-center justify-center gap-1 ${
              lead.ai_paused ? "bg-[#7A6B5F] text-white border-[#7A6B5F]" : "border-[#7A6B5F]/50 text-[#7A6B5F] hover:bg-[#7A6B5F]/10"
            }`}
          >
            {lead.ai_paused ? <><PlayCircle size={11} /> Resume AI</> : <><PauseCircle size={11} /> Pause AI</>}
          </button>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={() => setNewTask({ task_type: "call", due_date: new Date().toISOString().slice(0, 10), priority: "medium", notes: "" })}
            data-testid="crm-lead-addtask-btn"
            className="w-full px-3 py-2 text-[9px] tracking-[0.16em] uppercase border border-[#6E1F2C]/50 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white inline-flex items-center justify-center gap-1"
          >
            <Plus size={11} /> Follow-up task
          </button>
        </div>
      </div>

      {/* AI assists */}
      <div className="bg-[#1C110C] p-4 mb-6">
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#D4A963] mb-3 inline-flex items-center gap-2">
          <Sparkles size={12} /> AI assists
        </p>
        <div className="flex flex-wrap gap-2">
          {[["score", "Suggest score"], ["summary", "Summarise conversation"], ["reply", "Draft next reply"]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => runAi(k)}
              disabled={!!aiBusy}
              data-testid={`crm-ai-${k}-btn`}
              className="border border-[#D4A963]/50 text-[#EDE1C8] px-4 py-2 text-[9px] tracking-[0.2em] uppercase hover:bg-[#D4A963] hover:text-[#1C110C] disabled:opacity-50"
            >
              {aiBusy === k ? "Thinking…" : l}
            </button>
          ))}
        </div>
        {aiResult && (
          <div className="mt-3 bg-white/5 border border-[#D4A963]/30 p-4 text-sm text-[#EDE1C8]" data-testid="crm-ai-result">
            {aiResult.kind === "score" ? (
              <>
                <p>Suggested score: <strong className="uppercase text-[#D4A963]">{aiResult.score}</strong> · Confidence {aiResult.confidence}%</p>
                <p className="text-[#EDE1C8]/70 text-xs mt-1">{aiResult.reasoning}</p>
                <p className="text-[#EDE1C8]/70 text-xs mt-1">Next: {aiResult.suggested_next_action}</p>
                <button
                  type="button"
                  onClick={() => { patch({ lead_score: aiResult.score, ai_confidence: aiResult.confidence }); setAiResult(null); }}
                  data-testid="crm-ai-apply-score"
                  className="mt-2 bg-[#D4A963] text-[#1C110C] px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase"
                >
                  Apply score
                </button>
              </>
            ) : (
              <>
                <p className="whitespace-pre-wrap">{aiResult.text}</p>
                {aiResult.kind === "reply" && (
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard?.writeText(aiResult.text); }}
                    className="mt-2 bg-[#D4A963] text-[#1C110C] px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase"
                  >
                    Copy to clipboard
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: details + remarks + tasks */}
        <div className="space-y-6">
          <div className="bg-white border border-[#6E1F2C]/10 p-5">
            <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-3">Lead details</p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
              {[
                ["Service", label(SERVICE_CATEGORIES, lead.service_category)],
                ["Sub category", lead.sub_category],
                ["Email", lead.email],
                ["Property", lead.property_type],
                ["Budget", lead.budget_level],
                ["Urgency", lead.urgency],
                ["Source channel", lead.source_channel],
                ["Source page", lead.source_page],
                ["Photos received", lead.photos_received ? "Yes" : "No"],
                ["AI confidence", lead.ai_confidence != null ? `${lead.ai_confidence}%` : "—"],
                ["Created", (lead.created_at || "").slice(0, 10)],
              ].map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-[#7A6B5F]">{k}</dt>
                  <dd className="text-[#1C110C] capitalize">{v || "—"}</dd>
                </React.Fragment>
              ))}
            </dl>
            {lead.requirement_summary && (
              <p className="text-[12px] text-[#3E2A22] mt-3 border-t border-[#6E1F2C]/10 pt-3">{lead.requirement_summary}</p>
            )}
            {lead.ai_score_reasoning && (
              <div className="mt-3 border-t border-[#6E1F2C]/10 pt-3">
                <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-1">
                  AI score reasoning{lead.ai_scored_at ? ` · ${(lead.ai_scored_at || "").slice(0, 16).replace("T", " ")}` : ""}
                </p>
                <p className="text-[12px] text-[#3E2A22]" data-testid="crm-lead-ai-reasoning">{lead.ai_score_reasoning}</p>
                {lead.ai_suggested_next_action && (
                  <p className="text-[11px] text-[#7A6B5F] mt-1">Next: {lead.ai_suggested_next_action}</p>
                )}
              </div>
            )}
            {lead.conversation_summary && (
              <div className="mt-3 border-t border-[#6E1F2C]/10 pt-3">
                <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-1">AI conversation summary</p>
                <p className="text-[12px] text-[#3E2A22] whitespace-pre-wrap" data-testid="crm-lead-summary">{lead.conversation_summary}</p>
              </div>
            )}
          </div>

          <div className="bg-white border border-[#6E1F2C]/10 p-5">
            <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-3">Internal remarks</p>
            <div className="flex gap-2 mb-3">
              <input
                data-testid="crm-remark-input"
                className={inputCls}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addRemark()}
                placeholder="Add an internal note…"
              />
              <button type="button" onClick={addRemark} data-testid="crm-remark-add" className="bg-[#6E1F2C] text-white px-3 text-[10px] uppercase tracking-[0.2em]">Add</button>
            </div>
            <ul className="space-y-2">
              {(lead.internal_remarks || []).slice().reverse().map((r, idx) => (
                <li key={`${r.at}-${idx}`} className="text-[12px] text-[#3E2A22] border-l-2 border-[#D4A963] pl-3">
                  {r.text}
                  <span className="block text-[10px] text-[#7A6B5F]">{r.by} · {(r.at || "").slice(0, 16).replace("T", " ")}</span>
                </li>
              ))}
              {(lead.internal_remarks || []).length === 0 && <li className="text-xs text-[#7A6B5F] italic">No remarks yet.</li>}
            </ul>
          </div>

          <div className="bg-white border border-[#6E1F2C]/10 p-5">
            <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-3">Follow-up tasks</p>
            <ul className="space-y-2" data-testid="crm-lead-tasks">
              {tasks.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-2 text-[12px] border border-[#6E1F2C]/10 px-3 py-2">
                  <span className={t.status === "done" ? "line-through text-[#7A6B5F]" : "text-[#1C110C]"}>
                    <strong className="capitalize">{(t.task_type || "task").replace("_", " ")}</strong> · due {t.due_date} · {t.priority}
                    {t.notes ? ` — ${t.notes}` : ""}
                  </span>
                  {t.status === "open" && (
                    <button type="button" onClick={async () => { await crmApi.updateTask(t.id, { status: "done" }); reload(); }} data-testid={`crm-task-done-${t.id}`} className="text-[9px] tracking-[0.16em] uppercase text-[#3F6E2C] border border-[#3F6E2C]/40 px-2 py-1 hover:bg-[#3F6E2C] hover:text-white">
                      Done
                    </button>
                  )}
                </li>
              ))}
              {tasks.length === 0 && <li className="text-xs text-[#7A6B5F] italic">No tasks yet.</li>}
            </ul>
          </div>
        </div>

        {/* Right: conversation log */}
        <div className="bg-white border border-[#6E1F2C]/10 p-5 flex flex-col">
          <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-3 inline-flex items-center gap-2">
            <MessageSquare size={12} /> WhatsApp conversation log
          </p>
          <div className="flex-1 space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="crm-conversation-list">
            {convos.map((c) => {
              const st = SENDER_STYLES[c.sender_type] || SENDER_STYLES.customer;
              return (
                <div key={c.id} className={`border px-3 py-2 ${st.cls}`}>
                  <p className="text-[9px] tracking-[0.16em] uppercase text-[#7A6B5F] flex items-center gap-1.5">
                    {c.sender_type === "ai" && <Bot size={10} />}
                    {st.label} · {(c.timestamp || "").slice(0, 16).replace("T", " ")}
                    {c.message_type && c.message_type !== "text" && <span className="text-[#6E1F2C]">· {c.message_type}</span>}
                    {c.delivery_status && <span className="text-[#3F6E2C]">· {c.delivery_status}</span>}
                    {c.detected_intent && <span className="text-[#A6822F]">· intent: {c.detected_intent}</span>}
                  </p>
                  <p className="text-[13px] text-[#1C110C] mt-0.5 whitespace-pre-wrap">{c.message_text}</p>
                  {c.ai_action_taken && <p className="text-[10px] text-[#7A6B5F] mt-0.5">AI action: {c.ai_action_taken}</p>}
                </div>
              );
            })}
            {convos.length === 0 && <p className="text-xs text-[#7A6B5F] italic">No messages logged yet. Log the WhatsApp exchange below.</p>}
          </div>
          <div className="mt-3 border-t border-[#6E1F2C]/10 pt-3 space-y-2">
            <div className="bg-[#FAF5EC] border border-[#D4A963]/40 p-3" data-testid="crm-wa-reply-panel">
              <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-1.5 flex items-center gap-1.5">
                <Bot size={11} /> AI suggested WhatsApp reply
                {lead.ai_draft_at && <span className="text-[#7A6B5F] normal-case tracking-normal">· {(lead.ai_draft_at || "").slice(0, 16).replace("T", " ")}</span>}
              </p>
              <textarea
                rows={3}
                data-testid="crm-wa-draft-input"
                className={inputCls}
                value={waDraft}
                onChange={(e) => setWaDraft(e.target.value)}
                placeholder="No AI draft yet — click Regenerate, or type your own reply…"
              />
              <div className="flex items-center gap-2 flex-wrap mt-2">
                <button type="button" onClick={sendWa} disabled={!!waBusy || !waDraft.trim()} data-testid="crm-wa-send-btn" className="bg-[#3F6E2C] text-white px-4 py-2 text-[9px] tracking-[0.18em] uppercase hover:bg-[#325a23] disabled:opacity-50">
                  {waBusy === "send" ? "Sending…" : "Send via WhatsApp"}
                </button>
                <button type="button" onClick={regenerateWa} disabled={!!waBusy} data-testid="crm-wa-regen-btn" className="border border-[#6E1F2C]/40 text-[#6E1F2C] px-4 py-2 text-[9px] tracking-[0.18em] uppercase hover:bg-[#6E1F2C] hover:text-white disabled:opacity-50">
                  {waBusy === "regen" ? "Thinking…" : "Regenerate AI reply"}
                </button>
                {waMsg && <span className={`text-[11px] ${waMsg.startsWith("Failed") ? "text-[#A3141A]" : "text-[#3F6E2C]"}`} data-testid="crm-wa-send-result">{waMsg}</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <select data-testid="crm-msg-sender" className="bg-[#FAF5EC] border border-[#6E1F2C]/15 px-2 py-2 text-xs" value={msg.sender_type} onChange={(e) => setMsg({ ...msg, sender_type: e.target.value })}>
                <option value="customer">Customer</option>
                <option value="sales_staff">Sales Staff</option>
                <option value="admin">Admin</option>
                <option value="ai">AI</option>
              </select>
              <input
                data-testid="crm-msg-input"
                className={inputCls}
                value={msg.message_text}
                onChange={(e) => setMsg({ ...msg, message_text: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && addMessage()}
                placeholder="Log a message…"
              />
              <button type="button" onClick={addMessage} data-testid="crm-msg-add" className="bg-[#6E1F2C] text-white px-3 text-xs"><Send size={13} /></button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick task modal */}
      {newTask && (
        <div className="fixed inset-0 z-50 bg-[#1C110C]/60 flex items-center justify-center p-4" onClick={() => setNewTask(null)}>
          <div className="bg-white w-full max-w-[420px] border border-[#6E1F2C]/20 p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="crm-newtask-modal">
            <h3 className="font-serif-display text-lg text-[#1C110C]">Follow-up task — {lead.lead_ref}</h3>
            <Field label="Type">
              <select className={inputCls} value={newTask.task_type} onChange={(e) => setNewTask({ ...newTask, task_type: e.target.value })}>
                {["call", "whatsapp", "site_visit", "quotation", "measurement", "other"].map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </select>
            </Field>
            <Field label="Due date"><input type="date" data-testid="crm-newtask-due" className={inputCls} value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} /></Field>
            <Field label="Priority">
              <select className={inputCls} value={newTask.priority} onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}>
                {["high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Notes"><input className={inputCls} value={newTask.notes} onChange={(e) => setNewTask({ ...newTask, notes: e.target.value })} /></Field>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setNewTask(null)} className="px-4 py-2 text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F]">Cancel</button>
              <button type="button" onClick={saveTask} data-testid="crm-newtask-save" className="bg-[#6E1F2C] text-white px-4 py-2 text-[10px] tracking-[0.2em] uppercase">Save task</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
